// Assessment Status Response Parser Utilities

import { ASSESSMENT_STATUS_CONFIG } from "../config/assessmentStatusConfig";

import type {
  RawStatusResponse,
  ParsedResponse,
  AssessmentCompletionResponse,
} from "../types/assessmentStatusTypes";

/**
 * Parse raw API response to determine completion status
 * Handles multiple response formats with confidence scoring
 */
export function parseStatusResponse(
  rawData: any
): AssessmentCompletionResponse {
  if (!rawData || typeof rawData !== "object") {
    return {
      isComplete: false,
      message: "Invalid response format",
      confidence: 0,
      source: "invalid_response",
    };
  }

  const response = rawData as RawStatusResponse;

  // Try each possible field format with confidence scoring
  const parseAttempts: ParsedResponse[] = [];

  // Direct boolean fields (highest confidence)
  const booleanFields = [
    { field: "isComplete", value: response.isComplete },
    { field: "is_completed", value: response.is_completed },
    { field: "completed", value: response.completed },
    { field: "has_assessment", value: response.has_assessment },
    { field: "assessment_completed", value: response.assessment_completed },
  ];

  for (const { field, value } of booleanFields) {
    if (typeof value === "boolean") {
      parseAttempts.push({
        isComplete: value,
        confidence: 0.9, // High confidence for boolean fields
        source: field,
        rawValue: value,
      });
    }
  }

  // String status fields (medium confidence)
  const statusFields = [
    { field: "status", value: response.status },
    { field: "assessment_status", value: response.assessment_status },
    { field: "completion_status", value: response.completion_status },
  ];

  for (const { field, value } of statusFields) {
    if (typeof value === "string") {
      const isComplete = parseStatusString(value);
      parseAttempts.push({
        isComplete,
        confidence: 0.7, // Medium confidence for string parsing
        source: field,
        rawValue: value,
      });
    }
  }

  // Number fields (convert to boolean, lower confidence)
  const numberFields = [
    { field: "isComplete", value: response.isComplete },
    { field: "completed", value: response.completed },
  ];

  for (const { field, value } of numberFields) {
    if (typeof value === "number") {
      parseAttempts.push({
        isComplete: value > 0,
        confidence: 0.5, // Lower confidence for number conversion
        source: field,
        rawValue: value,
      });
    }
  }

  // Select the highest confidence result
  if (parseAttempts.length > 0) {
    const bestResult = parseAttempts.reduce((prev, current) =>
      current.confidence > prev.confidence ? current : prev
    );

    return {
      isComplete: bestResult.isComplete,
      message: response.message ?? "Status determined successfully",
      confidence: bestResult.confidence,
      source: bestResult.source,
    };
  }

  // Fallback: Conservative approach (assume incomplete)
  return {
    isComplete: false,
    message: "Unknown response format - assuming assessment not completed",
    confidence: 0.1,
    source: "fallback_safe",
  };
}

/**
 * Parse string status values to boolean
 */
function parseStatusString(status: string): boolean {
  const lowerStatus = status.toLowerCase().trim();

  // Check complete values
  if (
    ASSESSMENT_STATUS_CONFIG.RESPONSE_FORMATS.STATUS_VALUES.COMPLETE.includes(
      lowerStatus as any
    )
  ) {
    return true;
  }

  // Check incomplete values
  if (
    ASSESSMENT_STATUS_CONFIG.RESPONSE_FORMATS.STATUS_VALUES.INCOMPLETE.includes(
      lowerStatus as any
    )
  ) {
    return false;
  }

  // Fallback string matching
  const completeKeywords = ["complet", "finish", "done", "success"];
  const incompleteKeywords = ["incomplet", "pending", "progress", "start"];

  const hasCompleteKeyword = completeKeywords.some(keyword =>
    lowerStatus.includes(keyword)
  );

  const hasIncompleteKeyword = incompleteKeywords.some(keyword =>
    lowerStatus.includes(keyword)
  );

  if (hasCompleteKeyword && !hasIncompleteKeyword) {
    return true;
  }

  if (hasIncompleteKeyword && !hasCompleteKeyword) {
    return false;
  }

  // Default to false for safety
  return false;
}

/**
 * Validate response structure
 */
export function validateStatusResponse(data: any): boolean {
  if (!data || typeof data !== "object") {
    return false;
  }

  const response = data as RawStatusResponse;

  // Check if any known field exists
  const hasKnownField =
    ASSESSMENT_STATUS_CONFIG.RESPONSE_FORMATS.COMPLETION_FIELDS.some(
      field => response[field as keyof RawStatusResponse] !== undefined
    );

  return (
    hasKnownField ||
    response.status !== undefined ||
    response.assessment_status !== undefined ||
    response.completion_status !== undefined
  );
}

/**
 * Create error response for parsing failures
 */
export function createParsingErrorResponse(
  _error?: any
): AssessmentCompletionResponse {
  return {
    isComplete: false,
    message: "Failed to parse assessment status response",
    confidence: 0,
    source: "parsing_error",
  };
}
